import { auth } from "@/firebase/firebaseInit";
import { createRouter, createWebHashHistory } from "vue-router";
import store from '../store/index';

import HomePage from "../pages/HomePage.vue";
import VolunteerFDList from "../pages/volunteer/VolunteerFDList.vue";
import VolunteerSignUpWizard from "../pages/volunteer/VolunteerSignUpWizard.vue";
import LoginPage from "../pages/user/LoginPage.vue";
import UserRegistration from "../pages/user/UserRegistration.vue";
import UserProfile from "../pages/user/UserProfile.vue";
import CreateFD from "../pages/fd/CreateFD";
import ManageFDs from "../pages/fd/ManageFDs";
import FDDetails from "../pages/fd/FDDetails.vue";
import BeneficiaryManagement from "../pages/beneficiary/BeneficiaryManagement";
import BeneficiaryUpdate from "../pages/beneficiary/BeneficiaryUpdate";
import AdvancedSearch from "@/pages/beneficiary/AdvancedSearch.vue";
import ViewBeneficiary from "../pages/beneficiary/ViewBeneficiary";
import EventsPage from "../pages/events/EventsPage";
import ManageEvent from "../pages/events/ManageEvent";
import FeaturesPage from "../pages/features/FeaturesPage";
import SurveysPage from "../pages/surveys/SurveysPage";
import RemindersPage from "../pages/reminders/RemindersPage";
import OfferHelpPage from "../pages/volunteer/OfferHelpPage.vue";
import InterestsPage from "../pages/admin/InterestsPage.vue";
import EmailVerificationPage from "../pages/user/EmailVerificationPage.vue";

const routes = [
  {
    path: "/volunteer",
    name: "VolunteerFDList",
    component: VolunteerFDList
  },
  {
    path: "/volunteer/:fdId",
    name: "VolunteerSignUpWizard",
    component: VolunteerSignUpWizard,
    props: true
  },
  {
    path: "/login",
    name: "LoginPage",
    component: LoginPage
  },
  {
    path: "/register",
    name: "UserRegistration",
    component: UserRegistration
  },
  {
    path: "/",
    name: "HomePage",
    component: HomePage,
    meta: { requiresAuth: true }
  },
  {
    path: "/profile",
    name: "UserProfile",
    component: UserProfile,
    meta: { requiresAuth: true }
  },
  {
    path: "/fd/create",
    name: "CreateFD",
    component: CreateFD,
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: "/fds",
    name: "ManageFDs",
    component: ManageFDs,
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: "/fds/:id",
    name: "FDDetails",
    component: FDDetails,
    props: true,
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: "/beneficiary",
    name: "BeneficiaryManagement",
    component: BeneficiaryManagement,
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: "/beneficiary/:id",
    name: "ViewBeneficiary",
    component: ViewBeneficiary,
    props: true,
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: "/beneficiary/:id/update",
    name: "BeneficiaryUpdate",
    component: BeneficiaryUpdate,
    props: true,
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: "/beneficiary/advancedsearch",
    name: "AdvancedSearch",
    component: AdvancedSearch,
    props: true,
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: "/events",
    name: "EventsPage",
    component: EventsPage,
    props: true,
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: "/events/:id",
    name: "ManageEvent",
    component: ManageEvent,
    props: true,
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: "/surveys",
    name: "SurveysPage",
    component: SurveysPage,
    props: true,
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: "/reminders",
    name: "RemindersPage",
    component: RemindersPage,
    props: true,
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: "/features",
    name: "FeaturesPage",
    component: FeaturesPage,
    props: true,
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: "/offer-help",
    name: "OfferHelpPage",
    component: OfferHelpPage,
    meta: { requiresAuth: true }
  },
  {
    path: "/interests",
    name: "InterestsPage",
    component: InterestsPage,
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: "/verify-email",
    name: "EmailVerificationPage",
    component: EmailVerificationPage
  },
  {
    path: '/:notFound(.*)', component: { template: '<div class="notification is-warning m-4">Page not found.</div>' }
  }
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  await auth.authStateReady();
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin);

  if (auth.currentUser && (to.path === '/login' || to.path === '/register')) {
    next('/');
    return;
  }

  // Only enforce email verification for accounts created after this feature was introduced.
  // Users who existed before are exempt to avoid breaking existing access.
  const EMAIL_VERIFICATION_CUTOFF = new Date('2026-10-06T00:00:00Z');
  const accountCreated = auth.currentUser ? new Date(auth.currentUser.metadata.creationTime) : null;
  if (auth.currentUser && !auth.currentUser.emailVerified && accountCreated >= EMAIL_VERIFICATION_CUTOFF && to.path !== '/verify-email' && to.path !== '/login') {
    next('/verify-email');
    return;
  }

  if (requiresAuth && !auth.currentUser) {
    next("/volunteer");
    return;
  }

  if (requiresAdmin) {
    if (!store.getters['user/getCurrentUser']) {
      try {
        await store.dispatch('user/loadUserProfile');
      } catch (e) {
        console.error('[router] loadUserProfile failed:', e);
        next('/login');
        return;
      }
    }
    if (!store.getters['user/isCurrentUserAdmin']) {
      next("/");
      return;
    }
  }

  next();
})

export default router;
